/**
 * Route Execution Service - Fleet Premium
 * Servico de persistencia de estado de execucao de rotas
 *
 * Funcionalidades:
 * - Persistir estado de execucao no backend
 * - Recuperar estado ao reabrir
 * - Sincronizar entre dispositivos via polling
 * - Registrar eventos de execucao
 */

import routePlanService, {
  PLAN_STATUS,
  STOP_STATUS,
  VEHICLE_STATUS,
  EVENT_TYPES,
  EVENT_SEVERITY,
} from '../../common/services/routePlanService';

// ============================================================================
// CONSTANTS
// ============================================================================

export const EXECUTION_STATE = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

// Intervalo de polling para sincronizacao (ms)
export const SYNC_INTERVAL = 30000; // 30 segundos

// ============================================================================
// EXECUTION STATE MANAGEMENT
// ============================================================================

/**
 * Inicia a execucao de um plano de rota
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo no plano (route_plan_vehicles.id)
 * @returns {Promise<Object>} Estado atualizado
 */
export const startExecution = async (planId, vehicleId) => {
  try {
    // Pre-check: skip if plan is already executing (avoids 400 error)
    const plan = await routePlanService.getRoutePlan(planId);
    if (plan.status === PLAN_STATUS.EXECUTING) {
      return { success: true, vehicle: null, state: EXECUTION_STATE.IN_PROGRESS, alreadyExecuting: true };
    }

    await routePlanService.updateRoutePlanStatus(planId, PLAN_STATUS.EXECUTING);

    // Iniciar o veiculo
    const vehicle = await routePlanService.startVehicle(vehicleId);

    // Registrar evento de inicio
    await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.PLAN_STARTED,
      severity: EVENT_SEVERITY.INFO,
      message: 'Execucao iniciada',
      vehicleId,
      timestamp: new Date().toISOString(),
    });

    return { success: true, vehicle, state: EXECUTION_STATE.IN_PROGRESS };
  } catch (error) {
    console.error('[RouteExecution] Error starting execution:', error);
    throw error;
  }
};

/**
 * Pausa a execucao de um plano
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @param {Object} currentState - Estado atual (paradas concluidas, posicao, etc)
 * @returns {Promise<Object>} Estado atualizado
 */
export const pauseExecution = async (planId, vehicleId, currentState = {}) => {
  try {
    // Pre-check: skip if plan is NOT executing (already paused/approved)
    const plan = await routePlanService.getRoutePlan(planId);
    if (plan.status !== PLAN_STATUS.EXECUTING) {
      return { success: true, state: EXECUTION_STATE.PAUSED, alreadyPaused: true };
    }

    await routePlanService.updateRoutePlanStatus(planId, PLAN_STATUS.APPROVED);

    // Registrar evento de pausa (optional)
    try {
      await routePlanService.addRoutePlanEvent(planId, {
        type: 'execution_paused',
        severity: EVENT_SEVERITY.INFO,
        message: 'Execucao pausada',
        vehicleId,
        timestamp: new Date().toISOString(),
      });
    } catch (eventError) {
      console.warn('[RouteExecution] Pause event logging skipped:', eventError.message);
    }

    return { success: true, state: EXECUTION_STATE.PAUSED };
  } catch (error) {
    console.error('[RouteExecution] Error pausing execution:', error);
    throw error;
  }
};

/**
 * Retoma a execucao de um plano pausado
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @returns {Promise<Object>} Estado atualizado
 */
export const resumeExecution = async (planId, vehicleId) => {
  try {
    // Pre-check: skip if plan is already executing (avoids 400 error)
    const plan = await routePlanService.getRoutePlan(planId);
    if (plan.status === PLAN_STATUS.EXECUTING) {
      return { success: true, state: EXECUTION_STATE.IN_PROGRESS, alreadyExecuting: true };
    }

    await routePlanService.updateRoutePlanStatus(planId, PLAN_STATUS.EXECUTING);

    // Registrar evento de retomada (optional)
    try {
      await routePlanService.addRoutePlanEvent(planId, {
        type: 'execution_resumed',
        severity: EVENT_SEVERITY.INFO,
        message: 'Execucao retomada',
        vehicleId,
        timestamp: new Date().toISOString(),
      });
    } catch (eventError) {
      console.warn('[RouteExecution] Resume event logging skipped:', eventError.message);
    }

    return { success: true, state: EXECUTION_STATE.IN_PROGRESS };
  } catch (error) {
    console.error('[RouteExecution] Error resuming execution:', error);
    throw error;
  }
};

/**
 * Finaliza a execucao de um plano
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @param {Object} summary - Resumo da execucao (distancia real, duracao real)
 * @returns {Promise<Object>} Estado atualizado
 */
export const completeExecution = async (planId, vehicleId, summary = {}) => {
  try {
    // Finalizar o veiculo
    await routePlanService.completeVehicle(vehicleId, {
      actualDistance: summary.actualDistance,
      actualDuration: summary.actualDuration,
      completedAt: new Date().toISOString(),
    });

    // Verificar se todos os veiculos foram concluidos
    const vehicles = await routePlanService.getRoutePlanVehicles(planId);
    const allCompleted = vehicles.every(
      (v) => v.status === VEHICLE_STATUS.COMPLETED || v.status === VEHICLE_STATUS.CANCELLED
    );

    if (allCompleted) {
      // Atualizar status do plano para 'completed'
      await routePlanService.updateRoutePlanStatus(planId, PLAN_STATUS.COMPLETED);

      // Registrar evento de conclusao
      await routePlanService.addRoutePlanEvent(planId, {
        type: EVENT_TYPES.PLAN_COMPLETED,
        severity: EVENT_SEVERITY.INFO,
        message: 'Plano concluido',
        vehicleId,
        data: JSON.stringify(summary),
        timestamp: new Date().toISOString(),
      });
    }

    return {
      success: true,
      state: EXECUTION_STATE.COMPLETED,
      planCompleted: allCompleted,
    };
  } catch (error) {
    console.error('[RouteExecution] Error completing execution:', error);
    throw error;
  }
};

/**
 * Cancela a execucao de um plano
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @param {string} reason - Motivo do cancelamento
 * @returns {Promise<Object>} Estado atualizado
 */
export const cancelExecution = async (planId, vehicleId, reason = '') => {
  try {
    // Cancelar o veiculo
    await routePlanService.updateRoutePlanVehicle(planId, vehicleId, {
      status: VEHICLE_STATUS.CANCELLED,
      cancelledAt: new Date().toISOString(),
      cancellationReason: reason,
    });

    // Registrar evento de cancelamento
    await routePlanService.addRoutePlanEvent(planId, {
      type: 'execution_cancelled',
      severity: EVENT_SEVERITY.WARNING,
      message: `Execucao cancelada: ${reason || 'Sem motivo informado'}`,
      vehicleId,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      state: EXECUTION_STATE.CANCELLED,
    };
  } catch (error) {
    console.error('[RouteExecution] Error cancelling execution:', error);
    throw error;
  }
};

// ============================================================================
// STOP MANAGEMENT
// ============================================================================

/**
 * Marca uma parada como concluida
 * @param {number} planId - ID do plano
 * @param {number} stopId - ID da parada
 * @param {Object} data - Dados da conclusao (notas, comprovante)
 * @returns {Promise<Object>} Parada atualizada
 */
export const completeStop = async (planId, stopId, data = {}) => {
  try {
    const stop = await routePlanService.completeStop(stopId, {
      notes: data.notes,
      proofPhoto: data.proofPhoto,
      signature: data.signature,
      receiverName: data.receiverName,
      completedAt: new Date().toISOString(),
      completedPosition: data.position ? JSON.stringify(data.position) : null,
    });

    // Registrar evento de parada concluida
    await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.STOP_COMPLETED,
      severity: EVENT_SEVERITY.INFO,
      message: `Parada concluida: ${stop.name || stopId}`,
      stopId,
      data: JSON.stringify({ notes: data.notes }),
      timestamp: new Date().toISOString(),
    });

    return stop;
  } catch (error) {
    console.error('[RouteExecution] Error completing stop:', error);
    throw error;
  }
};

/**
 * Pula uma parada
 * @param {number} planId - ID do plano
 * @param {number} stopId - ID da parada
 * @param {string} reason - Motivo
 * @returns {Promise<Object>} Parada atualizada
 */
export const skipStop = async (planId, stopId, reason = '') => {
  try {
    const stop = await routePlanService.skipStop(stopId, reason);

    // Registrar evento de parada pulada
    await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.STOP_SKIPPED,
      severity: EVENT_SEVERITY.WARNING,
      message: `Parada pulada: ${stop.name || stopId} - ${reason || 'Sem motivo'}`,
      stopId,
      data: JSON.stringify({ reason }),
      timestamp: new Date().toISOString(),
    });

    return stop;
  } catch (error) {
    console.error('[RouteExecution] Error skipping stop:', error);
    throw error;
  }
};

/**
 * Marca chegada em uma parada
 * @param {number} stopId - ID da parada
 * @param {Object} position - Posicao de chegada
 * @returns {Promise<Object>} Parada atualizada
 */
export const arriveAtStop = async (stopId, position = null) => {
  try {
    const stop = await routePlanService.updateStopStatus(stopId, STOP_STATUS.ARRIVED);

    // Atualizar com posicao se fornecida
    if (position) {
      // Nota: precisaria de endpoint especifico para atualizar posicao
      // Por enquanto, apenas atualiza o status
    }

    return stop;
  } catch (error) {
    console.error('[RouteExecution] Error arriving at stop:', error);
    throw error;
  }
};

/**
 * Marca uma parada como em rota
 * @param {number} stopId - ID da parada
 * @returns {Promise<Object>} Parada atualizada
 */
export const setStopEnRoute = async (stopId) => {
  try {
    return await routePlanService.updateStopStatus(stopId, STOP_STATUS.EN_ROUTE);
  } catch (error) {
    console.error('[RouteExecution] Error setting stop en route:', error);
    throw error;
  }
};

// ============================================================================
// STATE RECOVERY
// ============================================================================

/**
 * Recupera o estado de execucao de um plano
 * @param {number} planId - ID do plano
 * @returns {Promise<Object>} Estado de execucao recuperado
 */
export const getExecutionState = async (planId) => {
  try {
    const summary = await routePlanService.getRoutePlanSummary(planId);

    if (!summary) {
      return null;
    }

    const { plan, vehicles, stops } = summary;

    // Determinar estado geral
    let executionState = EXECUTION_STATE.NOT_STARTED;
    if (plan.status === PLAN_STATUS.EXECUTING) {
      executionState = EXECUTION_STATE.IN_PROGRESS;
    } else if (plan.status === PLAN_STATUS.APPROVED) {
      // Se "approved" mas tem paradas concluidas/em rota, o plano foi pausado
      const hasProgress = stops.some(
        (s) => s.status === STOP_STATUS.COMPLETED || s.status === STOP_STATUS.SKIPPED || s.status === STOP_STATUS.EN_ROUTE
      );
      if (hasProgress) {
        executionState = EXECUTION_STATE.PAUSED;
      }
    } else if (plan.status === PLAN_STATUS.COMPLETED) {
      executionState = EXECUTION_STATE.COMPLETED;
    } else if (plan.status === PLAN_STATUS.CANCELLED) {
      executionState = EXECUTION_STATE.CANCELLED;
    }

    // Recuperar paradas concluidas
    const completedStops = stops.filter(
      (s) => s.status === STOP_STATUS.COMPLETED || s.status === STOP_STATUS.SKIPPED
    );

    // Encontrar parada atual (primeira pendente ou em rota)
    const currentStop = stops.find(
      (s) => s.status === STOP_STATUS.EN_ROUTE || s.status === STOP_STATUS.PENDING
    );

    // Calcular progresso
    const totalStops = stops.length;
    const completedCount = completedStops.length;
    const progressPercent = totalStops > 0 ? (completedCount / totalStops) * 100 : 0;

    return {
      plan,
      vehicles,
      stops,
      executionState,
      completedStops: completedStops.map((s) => s.id),
      currentStop,
      progress: {
        completed: completedCount,
        total: totalStops,
        percent: progressPercent,
      },
      lastUpdate: plan.updatedAt || plan.createdAt,
    };
  } catch (error) {
    console.error('[RouteExecution] Error getting execution state:', error);
    throw error;
  }
};

/**
 * Lista rotas em execucao do usuario
 * @returns {Promise<Array>} Lista de planos em execucao
 */
export const getActiveExecutions = async () => {
  try {
    const plans = await routePlanService.getRoutePlans({ status: PLAN_STATUS.EXECUTING });
    return plans;
  } catch (error) {
    console.error('[RouteExecution] Error getting active executions:', error);
    throw error;
  }
};

/**
 * Lista rotas pausadas do usuario
 * @returns {Promise<Array>} Lista de planos pausados
 */
export const getPausedExecutions = async () => {
  try {
    // Buscar planos em execucao
    const plans = await routePlanService.getRoutePlans({ status: PLAN_STATUS.EXECUTING });

    // Filtrar aqueles com veiculos pausados
    const pausedPlans = [];
    for (const plan of plans) {
      const vehicles = await routePlanService.getRoutePlanVehicles(plan.id);
      const hasPaused = vehicles.some((v) => v.status === 'paused');
      if (hasPaused) {
        pausedPlans.push({
          ...plan,
          pausedVehicles: vehicles.filter((v) => v.status === 'paused'),
        });
      }
    }

    return pausedPlans;
  } catch (error) {
    console.error('[RouteExecution] Error getting paused executions:', error);
    throw error;
  }
};

// ============================================================================
// SYNC AND POLLING
// ============================================================================

/**
 * Busca atualizacoes de um plano (para sincronizacao)
 * @param {number} planId - ID do plano
 * @param {string} lastSyncTime - Ultimo timestamp de sincronizacao
 * @returns {Promise<Object>} Atualizacoes desde o ultimo sync
 */
export const syncExecutionState = async (planId, lastSyncTime = null) => {
  try {
    // Buscar estado atual
    const currentState = await getExecutionState(planId);

    // Buscar eventos desde o ultimo sync
    const events = await routePlanService.getRoutePlanEvents(planId);
    const newEvents = lastSyncTime
      ? events.filter((e) => new Date(e.createdAt) > new Date(lastSyncTime))
      : events;

    return {
      state: currentState,
      events: newEvents,
      syncTime: new Date().toISOString(),
    };
  } catch (error) {
    console.error('[RouteExecution] Error syncing execution state:', error);
    throw error;
  }
};

/**
 * Cria um intervalo de polling para sincronizacao
 * @param {number} planId - ID do plano
 * @param {Function} onUpdate - Callback para atualizacoes
 * @param {number} interval - Intervalo em ms (default: 30s)
 * @returns {Function} Funcao para parar o polling
 */
export const startSyncPolling = (planId, onUpdate, interval = SYNC_INTERVAL) => {
  let lastSyncTime = new Date().toISOString();
  let isActive = true;

  const poll = async () => {
    if (!isActive) return;

    try {
      const updates = await syncExecutionState(planId, lastSyncTime);
      lastSyncTime = updates.syncTime;

      if (onUpdate) {
        onUpdate(updates);
      }
    } catch (error) {
      console.error('[RouteExecution] Polling error:', error);
    }

    if (isActive) {
      setTimeout(poll, interval);
    }
  };

  // Iniciar polling
  setTimeout(poll, interval);

  // Retornar funcao para parar
  return () => {
    isActive = false;
  };
};

// ============================================================================
// EVENT LOGGING
// ============================================================================

/**
 * Registra um evento de desvio de rota
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @param {Object} deviation - Dados do desvio
 * @returns {Promise<Object>} Evento criado
 */
export const logRouteDeviation = async (planId, vehicleId, deviation) => {
  try {
    return await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.ROUTE_DEVIATION,
      severity: deviation.distance > 500 ? EVENT_SEVERITY.WARNING : EVENT_SEVERITY.INFO,
      message: `Desvio detectado: ${deviation.distance}m da rota planejada`,
      vehicleId,
      data: JSON.stringify(deviation),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[RouteExecution] Error logging deviation:', error);
    throw error;
  }
};

/**
 * Registra um evento de atraso
 * @param {number} planId - ID do plano
 * @param {number} stopId - ID da parada
 * @param {number} delayMinutes - Minutos de atraso
 * @returns {Promise<Object>} Evento criado
 */
export const logLateArrival = async (planId, stopId, delayMinutes) => {
  try {
    return await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.LATE_ARRIVAL,
      severity: delayMinutes > 30 ? EVENT_SEVERITY.WARNING : EVENT_SEVERITY.INFO,
      message: `Chegada com ${delayMinutes} minutos de atraso`,
      stopId,
      data: JSON.stringify({ delayMinutes }),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[RouteExecution] Error logging late arrival:', error);
    throw error;
  }
};

/**
 * Registra um evento de re-routing
 * @param {number} planId - ID do plano
 * @param {number} vehicleId - ID do veiculo
 * @param {Object} reRouteData - Dados do re-routing
 * @returns {Promise<Object>} Evento criado
 */
export const logReRouting = async (planId, vehicleId, reRouteData) => {
  try {
    return await routePlanService.addRoutePlanEvent(planId, {
      type: EVENT_TYPES.RE_ROUTING,
      severity: EVENT_SEVERITY.INFO,
      message: `Rota recalculada: ${reRouteData.reason || 'Manual'}`,
      vehicleId,
      data: JSON.stringify(reRouteData),
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[RouteExecution] Error logging re-routing:', error);
    throw error;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Execution State
  startExecution,
  pauseExecution,
  resumeExecution,
  completeExecution,
  cancelExecution,
  // Stop Management
  completeStop,
  skipStop,
  arriveAtStop,
  setStopEnRoute,
  // State Recovery
  getExecutionState,
  getActiveExecutions,
  getPausedExecutions,
  // Sync
  syncExecutionState,
  startSyncPolling,
  // Event Logging
  logRouteDeviation,
  logLateArrival,
  logReRouting,
  // Constants
  EXECUTION_STATE,
  SYNC_INTERVAL,
};
